// Página MyOrderDetail - Detalle de un pedido del usuario
// Muestra el detalle completo de un pedido específico del usuario logueado

import { useState, useEffect, useCallback, useMemo } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { useBusinessRulesContext } from '../hooks/useBusinessRulesContext'
import { getOrder, updateOrder } from '../services/orders.service'
import { getWarehouse } from '../services/warehouses.service'
import { createMercadoPagoPreference } from '../services/mercadopago.service'
import { formatCurrency, formatDate } from '../utils/helpers'
import { getImageUrl } from '../services/cloudinary.service'
import Button from '../components/Button'
import Toast from '../components/Toast'
import PaymentMethodSelector from '../components/PaymentMethodSelector'
import './MyOrderDetail.css'

/**
 * Página MyOrderDetail
 * Muestra el detalle completo de un pedido del usuario
 */
function MyOrderDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { currentUser, loading: authLoading } = useAuth()
  const { rules } = useBusinessRulesContext()
  
  const [order, setOrder] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [pickupWarehouse, setPickupWarehouse] = useState(null)
  const [notification, setNotification] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState('')

  const enabledPaymentMethods = useMemo(() => {
    if (rules?.paymentMethods?.enabled === false) return []
    const methods = Array.isArray(rules?.paymentMethods?.methods) ? rules.paymentMethods.methods : []
    return methods.filter(m => m?.enabled !== false)
  }, [rules])

  const selectedPaymentMethod = useMemo(() => {
    if (!selectedPaymentMethodId) return null
    return enabledPaymentMethods.find(m => m.id === selectedPaymentMethodId) || null
  }, [enabledPaymentMethods, selectedPaymentMethodId])

  const traceParams = useMemo(() => {
    const params = new URLSearchParams(window.location.search)
    const raw = params.get('trace') || params.get('debug')
    const enabled = raw === '1' || raw === 'true'
    const traceId = params.get('traceId') || params.get('trace_id')
    return { enabled, traceId: traceId || null }
  }, [])

  const fetchOrder = useCallback(async () => {
    if (!currentUser || !id) return
    try {
      setLoading(true)
      setError(null)
      const orderData = await getOrder(id)
      
      if (!orderData) {
        setError('Pedido no encontrado')
        return
      }

      // Verificar que el pedido pertenece al usuario
      if (orderData.customer?.email !== currentUser.email) {
        setError('No tienes permiso para ver este pedido')
        return
      }
      
      setOrder(orderData)
    } catch (err) {
      console.error('Error cargando pedido:', err)
      setError('Error al cargar el pedido')
    } finally {
      setLoading(false)
    }
  }, [currentUser, id])

  // Redirigir si no está logueado
  useEffect(() => {
    if (!authLoading && !currentUser) {
      navigate('/login')
    }
  }, [currentUser, authLoading, navigate])

  // Cargar el pedido
  useEffect(() => {
    if (currentUser && id) {
      fetchOrder()
    }
  }, [currentUser, id, fetchOrder])

  useEffect(() => {
    if (!order || !enabledPaymentMethods.length) return
    const fromOrder = order.payment?.methodId
    const match = enabledPaymentMethods.find(m => m.id === fromOrder) || enabledPaymentMethods[0]
    if (!selectedPaymentMethodId || selectedPaymentMethodId !== match.id) {
      setSelectedPaymentMethodId(match.id)
    }
  }, [order, enabledPaymentMethods, selectedPaymentMethodId])

  const calculatePaymentAdjustment = useCallback((subtotal, priceAdjustment) => {
    if (!priceAdjustment) return 0
    const rawValue = Number(priceAdjustment.value)
    if (!Number.isFinite(rawValue) || rawValue === 0) return 0
    const baseAmount = priceAdjustment.kind === 'fixed' ? rawValue : (subtotal * rawValue) / 100
    const sign = priceAdjustment.sign === 'surcharge' ? 1 : -1
    return baseAmount * sign
  }, [])

  const buildOrderSnapshot = (source) => {
    if (!source) return null
    const total = Number(source.total)
    if (!Number.isFinite(total) || total <= 0) return null
    return {
      total,
      orderNumber: source.orderNumber || null,
      customerEmail: source.customer?.email || null,
      paymentType: source.payment?.type || null
    }
  }

  const buildGatewayPayload = (mp) => ({
    provider: 'mercadopago',
    preferenceId: mp?.preferenceId || null,
    initPoint: mp?.initPoint || null,
    sandboxInitPoint: mp?.sandboxInitPoint || null,
    status: 'created'
  })

  useEffect(() => {
    let cancelled = false
    const run = async () => {
      const method = order?.fulfillment?.method === 'pickup' ? 'pickup' : 'delivery'
      const pickupWarehouseId = typeof order?.fulfillment?.pickupWarehouseId === 'string' ? order.fulfillment.pickupWarehouseId : ''
      if (method !== 'pickup' || !pickupWarehouseId) {
        setPickupWarehouse(null)
        return
      }
      try {
        const wh = await getWarehouse(pickupWarehouseId)
        if (!cancelled) setPickupWarehouse(wh || null)
      } catch (_e) {
        if (!cancelled) setPickupWarehouse(null)
      }
    }
    run()
    return () => {
      cancelled = true
    }
  }, [order])

  const getStatusLabel = (status) => {
    const labels = {
      pending: 'Pendiente',
      confirmed: 'Confirmado',
      processing: 'En Proceso',
      shipped: 'Enviado',
      delivered: 'Entregado',
      cancelled: 'Cancelado'
    }
    return labels[status] || status
  }

  const getStatusClass = (status) => {
    return `status-badge status-${status}`
  }

  const isMercadoPagoPayment = order?.payment?.type === 'gateway_mercadopago'
  const paymentGatewayStatus = order?.paymentGateway?.status || null

  const getPaymentStatusLabel = (status) => {
    if (!status) return 'Esperando confirmación de pago'
    if (status === 'approved') return 'Pago aprobado'
    if (status === 'created') return 'Pago pendiente'
    if (status === 'in_process') return 'Pago en proceso'
    if (status === 'pending') return 'Pago pendiente'
    if (status === 'rejected') return 'Pago rechazado'
    if (status === 'cancelled') return 'Pago cancelado'
    if (status === 'refunded') return 'Pago reembolsado'
    return 'Estado de pago desconocido'
  }

  const getPaymentStatusClassName = (status) => {
    if (status === 'approved') return 'status-badge status-delivered'
    if (status === 'rejected' || status === 'cancelled') return 'status-badge status-cancelled'
    return 'status-badge status-pending'
  }

  const handleChangePaymentMethod = async () => {
    if (!order || !selectedPaymentMethod || submitting) return
    setSubmitting(true)
    try {
      const subtotal = Number(order.subtotal || 0)
      const shipping = Number(order.shipping || 0)
      const adjustmentAmount = calculatePaymentAdjustment(subtotal, selectedPaymentMethod.priceAdjustment)
      const total = Math.max(0, subtotal + shipping + adjustmentAmount)
      const payment = {
        methodId: selectedPaymentMethod.id,
        type: selectedPaymentMethod.type,
        label: selectedPaymentMethod.label,
        customerNote: selectedPaymentMethod.customerNote || '',
        priceAdjustment: selectedPaymentMethod.priceAdjustment || null,
        selectedAt: new Date()
      }
      const paymentAdjustment = selectedPaymentMethod.priceAdjustment && adjustmentAmount
        ? { ...selectedPaymentMethod.priceAdjustment, amount: adjustmentAmount }
        : null
      const orderUpdates = {
        payment,
        paymentAdjustment,
        total,
        paymentGateway: null
      }
      await updateOrder(order.id, orderUpdates)
      const updatedOrder = { ...order, ...orderUpdates }
      setOrder(updatedOrder)

      if (selectedPaymentMethod.type === 'gateway_mercadopago') {
        const orderSnapshot = buildOrderSnapshot(updatedOrder)
        const mp = await createMercadoPagoPreference({
          orderId: order.id,
          storefrontUrl: window.location.origin,
          orderSnapshot,
          trace: traceParams.enabled,
          traceId: traceParams.traceId
        })
        const gatewayPayload = buildGatewayPayload(mp)
        await updateOrder(order.id, { paymentGateway: gatewayPayload }).catch(() => null)
        const redirectUrl = mp?.redirectUrl || mp?.sandboxInitPoint || mp?.initPoint
        if (!redirectUrl) {
          throw new Error('MercadoPago no devolvió un link de pago')
        }
        window.location.href = redirectUrl
        return
      }

      setNotification({ type: 'success', message: 'Medio de pago actualizado' })
      setTimeout(() => setNotification(null), 5000)
    } catch (err) {
      setNotification({
        type: 'error',
        message: `No se pudo actualizar el medio de pago: ${err?.message || 'Error desconocido'}`
      })
      setTimeout(() => setNotification(null), 6000)
    } finally {
      setSubmitting(false)
    }
  }

  const canChangePayment = useMemo(() => {
    if (order?.status === 'cancelled' || order?.status === 'delivered') return false
    if (paymentGatewayStatus === 'approved') return false
    return true
  }, [order, paymentGatewayStatus])

  const isPaymentRejected = useMemo(() => {
    return order?.status === 'payment_rejected' || paymentGatewayStatus === 'rejected'
  }, [order, paymentGatewayStatus])

  if (authLoading || loading) {
    return (
      <div className="my-order-detail-page">
        <div className="loading-state">
          <p>Cargando pedido...</p>
        </div>
      </div>
    )
  }

  if (error || !order) {
    return (
      <div className="my-order-detail-page">
        <div className="error-state">
          <h2>Error</h2>
          <p>{error || 'No se pudo cargar la información del pedido'}</p>
          <Button onClick={() => navigate('/mis-pedidos')}>
            Volver a Mis Pedidos
          </Button>
        </div>
      </div>
    )
  }

  const orderDate = order.createdAt?.toDate 
    ? formatDate(order.createdAt.toDate())
    : formatDate(new Date())
  const fulfillmentMethod = order?.fulfillment?.method === 'pickup' ? 'pickup' : 'delivery'

  return (
    <div className="my-order-detail-page">
      <Toast
        isVisible={!!notification}
        message={notification?.message || ''}
        type={notification?.type || 'error'}
        onClose={() => setNotification(null)}
      />
      {/* Header */}
      <div className="detail-header">
        <div>
          <Link to="/mis-pedidos" className="back-link">
            ← Volver a Mis Pedidos
          </Link>
          <h1>Pedido {order.orderNumber || order.id}</h1>
          <p className="detail-subtitle">Creado el {orderDate}</p>
        </div>
        <div className="detail-status-section">
          <span className={getStatusClass(order.status)}>
            {getStatusLabel(order.status)}
          </span>
        </div>
      </div>

      <div className="detail-content">
        {/* Información del pedido */}
        <div className="detail-section">
          <h2 className="section-title">Información del Pedido</h2>
          <div className="info-grid">
            <div className="info-item">
              <label>Número de Pedido:</label>
              <span className="monospace">{order.orderNumber || order.id}</span>
            </div>
            <div className="info-item">
              <label>Fecha:</label>
              <span>{orderDate}</span>
            </div>
            <div className="info-item">
              <label>Estado:</label>
              <span className={getStatusClass(order.status)}>
                {getStatusLabel(order.status)}
              </span>
            </div>
            {isMercadoPagoPayment && (
              <div className="info-item">
                <label>Estado del pago:</label>
                <span className={getPaymentStatusClassName(paymentGatewayStatus)}>
                  {getPaymentStatusLabel(paymentGatewayStatus)}
                </span>
              </div>
            )}
            {order.updatedAt && (
              <div className="info-item">
                <label>Última Actualización:</label>
                <span>
                  {order.updatedAt?.toDate 
                    ? formatDate(order.updatedAt.toDate())
                    : formatDate(new Date())}
                </span>
              </div>
            )}
          </div>
        </div>

        {/* Dirección de envío */}
        <div className="detail-section">
          <h2 className="section-title">Datos de Entrega</h2>
          <div className="info-details">
            <p><strong>{order.customer?.name || 'N/A'}</strong></p>
            <p>{order.customer?.email || 'N/A'}</p>
            <p>{order.customer?.phone || 'N/A'}</p>
            {fulfillmentMethod === 'pickup' ? (
              <p className="address">
                Retiro por sucursal
                {pickupWarehouse?.name ? `: ${pickupWarehouse.name}` : ''}
                {pickupWarehouse?.address ? ` — ${pickupWarehouse.address}` : ''}
              </p>
            ) : (
              <p className="address">
                Envío a domicilio: {order.customer?.address?.street || ''}
                {order.customer?.address?.city && `, ${order.customer.address.city}`}
                {order.customer?.address?.postalCode && ` (${order.customer.address.postalCode})`}
                {order.customer?.address?.province && `, ${order.customer.address.province}`}
              </p>
            )}
          </div>
        </div>

        {/* Productos del pedido */}
        <div className="detail-section">
          <h2 className="section-title">Productos del Pedido</h2>
          <div className="order-items-list">
            {order.items?.map((item, index) => {
              // Obtener la imagen del item
              let imageUrl = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400"%3E%3Crect fill="%23f5f0eb" width="400" height="400"/%3E%3C/svg%3E'
              
              if (item.image) {
                // Si el image es un fileId sin carpeta, intentar agregar la carpeta "products/"
                let imageId = item.image
                if (imageId && !imageId.includes('/') && !imageId.startsWith('http')) {
                  imageId = `products/${imageId}`
                }
                imageUrl = getImageUrl(imageId, { width: 100, height: 100, crop: 'fill' })
              }

              return (
                <div key={index} className="order-item-detail">
                  <img 
                    src={imageUrl} 
                    alt={item.productName}
                    className="item-image"
                  />
                  <div className="item-info">
                    <h3 className="item-name">{item.productName}</h3>
                    {item.sku && (
                      <p className="item-sku">SKU: {item.sku}</p>
                    )}
                    {item.variant && (
                      <p className="item-variant">
                        {item.variant.type}: {item.variant.name}
                      </p>
                    )}
                    <p className="item-quantity">Cantidad: {item.quantity}</p>
                  </div>
                  <div className="item-price">
                    <p className="item-unit-price">
                      {formatCurrency(item.price)} c/u
                    </p>
                    <p className="item-total-price">
                      {formatCurrency(item.price * item.quantity)}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        </div>

        {/* Resumen financiero */}
        <div className="detail-section">
          <h2 className="section-title">Resumen Financiero</h2>
          <div className="financial-summary">
            <div className="summary-row">
              <span className="summary-label">Subtotal:</span>
              <span className="summary-value">{formatCurrency(order.subtotal || 0)}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">{fulfillmentMethod === 'pickup' ? 'Retiro:' : 'Envío:'}</span>
              <span className="summary-value">
                {order.shipping === 0 ? (
                  <span className="free-shipping">Gratis</span>
                ) : (
                  formatCurrency(order.shipping || 0)
                )}
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Medio de pago:</span>
              <span className="summary-value">{order.payment?.label || 'Pendiente'}</span>
            </div>
            {order.paymentAdjustment?.amount ? (
              <div className="summary-row">
                <span className="summary-label">{order.paymentAdjustment.amount < 0 ? 'Descuento:' : 'Recargo:'}</span>
                <span className="summary-value">
                  {order.paymentAdjustment.amount < 0 ? '-' : '+'}{formatCurrency(Math.abs(order.paymentAdjustment.amount))}
                </span>
              </div>
            ) : null}
            {order.distance && (
              <div className="summary-row">
                <span className="summary-label">Distancia:</span>
                <span className="summary-value">{order.distance} km</span>
              </div>
            )}
            <div className="summary-divider"></div>
            <div className="summary-row summary-total">
              <span className="summary-label">Total:</span>
              <span className="summary-value">{formatCurrency(order.total || 0)}</span>
            </div>
          </div>
        </div>

        {/* Notas adicionales */}
        {order.notes && (
          <div className="detail-section">
            <h2 className="section-title">Notas Adicionales</h2>
            <p className="order-notes">{order.notes}</p>
          </div>
        )}
      </div>

      {canChangePayment && enabledPaymentMethods.length > 0 ? (
        <div className="detail-section">
          <h2 className="section-title">
            {isPaymentRejected ? 'Reintentar Pago' : 'Cambiar medio de pago'}
          </h2>
          {isPaymentRejected && (
            <p className="payment-rejected-message">
              Tu último intento de pago fue rechazado. Puedes seleccionar el mismo medio de pago u otro para volver a intentarlo.
            </p>
          )}
          <PaymentMethodSelector
            methods={enabledPaymentMethods}
            selectedMethodId={selectedPaymentMethodId}
            onSelectMethodId={setSelectedPaymentMethodId}
            onNotify={(n) => setNotification(n)}
            formatCurrency={formatCurrency}
            disabled={submitting}
          />
          <div className="detail-actions">
            <Button variant="primary" onClick={handleChangePaymentMethod} disabled={submitting}>
              {submitting 
                ? 'Procesando...' 
                : (selectedPaymentMethod?.type === 'gateway_mercadopago'
                  ? (isPaymentRejected ? 'Reintentar pago con MercadoPago' : 'Pagar con MercadoPago')
                  : 'Actualizar medio de pago')}
            </Button>
          </div>
        </div>
      ) : null}

      <div className="detail-actions">
        <Link to="/mis-pedidos">
          <Button variant="outline">
            ← Volver a Mis Pedidos
          </Button>
        </Link>
        <Link to="/productos">
          <Button variant="primary">
            Seguir Comprando
          </Button>
        </Link>
      </div>
    </div>
  )
}

export default MyOrderDetail

